import { HttpTransport } from '@estee/elc-http-transport';
import * as graphql from 'graphql.js';
import { root } from '@estee/elc-universal-utils';
import { LoyaltyFieldString, ILoyaltyApiResponse } from '~api/interfaces/ILoyaltyApiResponse';
import {
    ILoyaltyEnrollmentResponse,
    IUserLoyaltyEnrollment
} from '~api/interfaces/ILoyaltyEnrollmentResponse';
import { ILoyaltyBenefitsResponse } from '~api/interfaces/ILoyaltyBenefitsResponse';
import { ILoyaltyRewardsResponse } from '~api/interfaces/ILoyaltyRewardsResponse';
import { IPointsHistoryResponse } from '~api/interfaces/IPointsHistoryResponse';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/LoyaltyService';
import {
    ILoyaltyRedeemOffersRequest,
    ILoyaltyRedeemOfferApiResponse,
    ILoyaltyUnredeemOfferApiRequest,
    ILoyaltyUnredeemOfferApiResponse
} from '~api/interfaces/ILoyaltyCartAPI';
import { ILoyaltyUserAuth } from '~api/interfaces/ILoyaltyUserAuth';
import { IBirthDate } from '~exported/loyalty-birthday/ILoyaltyBirthday';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { QueryFragments } from '~api/QueryFragments';
import { UserLoyaltyInfo } from '~api/query/UserLoyaltyInfo';
import { UserEnrollment } from '~api/query/UserEnrollment';
import { PointsHistory } from '~api/query/PointsHistory';
import { LoyaltyRewards } from '~api/query/LoyaltyRewards';
import { LoyaltyBenefits } from '~api/query/LoyaltyBenefits';
import { RedeemOffers } from '~api/query/RedeemOffer';
import { UnredeemOffers } from '~api/query/UnredeemOffer';
import { SetUserBirthdate } from '~api/query/SetUserBirthdate';
import { UserAuth } from '~api/query/UserAuth';
import { IJWTApiRequest, IJWTApiResponse } from '~api/interfaces/IJWTToken';
import { ISetBirthdateResponse } from '~api/interfaces/ISetBirthdateResponse';

const BASE_API_PATH = '/graphql';
const JWT_API_PATH = '/rest/api/v1/jwt';

export class LoyaltyApiSdk {
    private httpTransport: HttpTransport;
    private queryFragments: QueryFragments;
    private queries = new Map();
    private configRepository: ConfigRepository;
    private JWTToken: LoyaltyFieldString;
    private JWTAPIEnabled: boolean = false;

    constructor(httpTransport: HttpTransport) {
        this.httpTransport = httpTransport;
        this.configRepository = diContainer.get(serviceNames.configRepository);
    }

    public setQueryFragments(queryFragments: QueryFragments) {
        this.queryFragments = queryFragments;
    }

    private getBaseUrl() {
        return `${root.location.protocol}//${root.location.host}`;
    }

    private get composedUrl() {
        return `${this.getBaseUrl()}${BASE_API_PATH}`;
    }

    private get headers() {
        return {
            'Business-Unit': this.configRepository.config.businessUnitId,
            ... (this.JWTAPIEnabled && this.JWTToken && {'Auth-Data': this.JWTToken} || this.configRepository.config.pgGraphqlJWT && {'Auth-Data': this.configRepository.config.pgGraphqlJWT})
        };
    }

    private get client() {
        const queryFragments = this.queryFragments?.enabledFragments;

        return ((this.JWTAPIEnabled && this.JWTToken || !this.JWTAPIEnabled) && graphql(this.composedUrl, {
            headers: this.headers,
            fragments: {
                ...queryFragments
            },
            asJSON: true
        }));
    }

    private clientQuery =
        (query: string, useQueryCache: boolean = false) =>
        async () => {
            if (useQueryCache && this.queries.has(query)) {
                return this.queries.get(query);
            }

            const queryPromise = new Promise((resolve) => {
                resolve(this.client(query)());
            });

            this.queries.set(query, queryPromise);

            return queryPromise;
        };

    private httpTransportRequest =
        (params: IJWTApiRequest, key: string, useQueryCache: boolean = false) =>
        async () => {
            if (useQueryCache && this.queries.has(key)) {
                return this.queries.get(key);
            }

            const httpTransportPromise = new Promise((resolve) => {
                const url = `${this.getBaseUrl()}${JWT_API_PATH}`;
                const response =  this.httpTransport.get({
                    url,
                    params
                });
                resolve(response);
            });

            this.queries.set(key, httpTransportPromise);

            return httpTransportPromise;
    };

    public getLoyaltyUserData = async (): Promise<ILoyaltyApiResponse> => {
        try {
            const queryFragments = this.queryFragments.queryFragments;
            const userQuery = UserLoyaltyInfo(queryFragments);
            const response = await this.clientQuery(userQuery, false)();

            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    public userLoyaltyEnrollment = async (
        loyaltyEnrollmentData: IUserLoyaltyEnrollment
    ): Promise<ILoyaltyEnrollmentResponse> => {
        try {
            const userEnrollmentQuery = UserEnrollment(loyaltyEnrollmentData);

            const response = await this.clientQuery(userEnrollmentQuery, true)();

            if (response[0]?.message) {
                response.errorMessage = response[0]?.message;
            }

            return Promise.resolve(response);
        } catch (error) {
            return Promise.resolve(error);
        }
    };

    public getPointsHistoryData = async (): Promise<IPointsHistoryResponse> => {
        try {
            const pointsHistoryQuery = PointsHistory();
            const response = await this.clientQuery(pointsHistoryQuery, false)();

            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    public getLoyaltyRewardOffers = async (
        rewardOfferType: string
    ): Promise<ILoyaltyRewardsResponse> => {
        try {
            const loyaltyRewardOfferQuery = LoyaltyRewards(rewardOfferType);
            const response = await this.clientQuery(loyaltyRewardOfferQuery, false)();

            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    public getLoyaltyBenefitsOffers = async (
        benefitOfferType: string
    ): Promise<ILoyaltyBenefitsResponse> => {
        try {
            const loyaltyBenefitsOfferQuery = LoyaltyBenefits(benefitOfferType);
            const response = await this.clientQuery(loyaltyBenefitsOfferQuery, false)();

            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    public getJWTToken = async (): Promise<IJWTApiResponse> => {
        try {

            const params = {
                brand: `${this.configRepository.config.brandId}`,
                region:  `${this.configRepository.config.regionId}`
            };

            return await this.httpTransportRequest(params, 'JWTRequest', true)();
        } catch (error) {
            return Promise.reject(error);
        }
    };

    public setJWTToken = (JWTAPIEnabled: boolean, token:  LoyaltyFieldString) => {
        this.JWTAPIEnabled = JWTAPIEnabled;
        this.JWTToken = token;
    };

    public getUserAuthState = async (): Promise<ILoyaltyUserAuth> => {
        try {
            const userAuthInfoQuery = UserAuth();
            const response = await this.clientQuery(userAuthInfoQuery, true)();

            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    public redeemOffer =  async (
        redeemOffers: ILoyaltyRedeemOffersRequest
    ): Promise<ILoyaltyRedeemOfferApiResponse> => {
        try {
            const redeemQuery = RedeemOffers(redeemOffers);
            const response = await this.clientQuery(redeemQuery, false)();

            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    public unredeemOffer = async (unredeemOffers: ILoyaltyUnredeemOfferApiRequest): Promise<ILoyaltyUnredeemOfferApiResponse> => {
        try {
            const unredeemsQuery = UnredeemOffers(unredeemOffers);
            const response = await this.clientQuery(unredeemsQuery, false)();

            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    public setUserBirthdate = async (birthday: IBirthDate): Promise<ISetBirthdateResponse> => {
        try {
            const setUserBirthdateQuery = SetUserBirthdate(birthday);
            const response = await this.clientQuery(setUserBirthdateQuery, false)();

            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    };
}

export const vulcan_csrf_token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
